import React from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import countries from 'countries-list';
import type { CustomerFormData, ValidationErrors } from '../types';

interface ContactInformationProps {
    formData: CustomerFormData;
    errors: ValidationErrors;
    onInputChange: (field: string, value: string) => void;
    onPhoneNumberInput: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onGLNInput: (e: React.FormEvent<HTMLInputElement>) => void;
}

const ContactInformation: React.FC<ContactInformationProps> = ({
    formData,
    errors,
    onInputChange,
    onPhoneNumberInput,
    onGLNInput
}) => {
    const { t } = useTranslation();
    
    // Generate country options for the postal country dropdown (same as BusinessInformation)
    const newDetails: any[] = [];
    Object.keys(countries.countries).forEach((code) => {
        newDetails.push((countries.countries as any)[code]);
    });
   
    const countryOptions = [
        { value: "Sweden", label: "Sweden" },
        { value: "USA", label: "USA" },
        { value: "Canada", label: "Canada" },
        { value: "Panama", label: "Panama" },
        ...newDetails.map((country) => ({
            value: country.name,
            label: country.name,
        })),
    ];
    
    return (
        
        <>
            <Row className="mb-4">
                <Col md={6}>
                    <Form.Group className="input-field">
                        <Form.Label>{t('CUSTOMER_REFERENCE_NAME_LABEL')}</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={t('TYPE_HERE')}
                            value={formData?.reference}
                            onChange={(e) => onInputChange('reference', e.target.value)}
                            onKeyDown={(e) => {
                                const allowedCharacters = /^[a-zA-Z0-9\s,.\-/']$/;
                                if (
                                    e.key !== 'Backspace' &&
                                    !allowedCharacters.test(e.key) &&
                                    !['ArrowLeft', 'ArrowRight', 'Tab', 'Delete', 'Control', 'v', 'Meta', 'c', 'x', 'a'].includes(e.key) &&
                                    !(e.ctrlKey || e.metaKey)
                                ) {
                                    e.preventDefault();
                                }
                            }}
                        />
                        <Form.Text className="text-muted">
                            {t('CUSTOMER_REFERENCE_INFO')}
                        </Form.Text>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="input-field">
                        <Form.Label>{t('CONTACT_EMAIL')}</Form.Label>
                        <Form.Control
                            type="email"
                            className={errors.contactEmail ? 'is-invalid' : ''}
                            placeholder={t('TYPE_HERE')}
                            value={formData.contactEmail}
                            onChange={(e) => onInputChange('contactEmail', e.target.value)}
                        />
                        <Form.Text className="text-muted">
                            {t('CONTACT_EMAIL_INFO')}
                        </Form.Text>
                        {errors.contactEmail && (
                            <Form.Text className="text-danger">{t(errors.contactEmail)}</Form.Text>
                        )}
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={6}>
                    <Form.Group className="input-field">
                        <Form.Label>{t('CONTACT_PHONE_NUMBER')}</Form.Label>
                        <Form.Control
                            type="tel"
                            className={errors.contactPhone ? 'is-invalid' : ''}
                            placeholder={t('TYPE_HERE')}
                            value={formData.phoneNumber}
                            onChange={(e) => onInputChange('phoneNumber', e.target.value)}
                            onKeyDown={onPhoneNumberInput}
                        />
                        {errors.phoneNumber && (
                            <Form.Text className="text-danger">{t(errors.phoneNumber)}</Form.Text>
                        )}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="input-field">
                        <Form.Label>{t('SELECT_INVOICE_LANGUAGE')}</Form.Label>
                        <Form.Select
                            className={`custom-select ${errors.invoiceLanguage ? 'is-invalid' : ''}`}
                            value={formData.invoiceLanguage}
                            onChange={(e) => onInputChange('invoiceLanguage', e.target.value)}
                        >
                            <option value="">{t('SELECT_LANGUAGE_TYPE')}</option>
                            <option value="sv-SE">{t('SWEDISH')}</option>
                            <option value="en-US">{t('ENGLISH')}</option>
                        </Form.Select>
                        {errors.invoiceLanguage && (
                            <Form.Text className="text-danger">{t(errors.invoiceLanguage)}</Form.Text>
                        )}
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={12}>
                    <Form.Group className="input-field">
                        <Form.Label>{t('HOW_DO_YOU_WANT_INVOICE')}</Form.Label>
                        <Form.Select
                            className={`custom-select ${errors.invoiceDeliveryMethod ? 'is-invalid' : ''}`}
                            value={formData.invoiceDeliveryMethod}
                            onChange={(e) => onInputChange('invoiceDeliveryMethod', e.target.value)}
                        >
                            <option value="">{t('SELECT_INVOICE_TYPE')}</option>
                            <option value="1">{t('EMAIL_SEND_TO_EMAIL')}</option>
                            <option value="2">{t('E_INVOICE_SEND_ELECTRONIC')}</option>
                            <option value="3">{t('POST_60_SEK')}</option>
                        </Form.Select>
                        {errors.invoiceDeliveryMethod && (
                            <Form.Text className="text-danger">{t(errors.invoiceDeliveryMethod)}</Form.Text>
                        )}
                    </Form.Group>
                </Col>
            </Row>

            {/* Email delivery specific fields */}
            {formData.invoiceDeliveryMethod === '1' && (
                <>
                    <Row className="mb-4">
                        <Col md={12}>
                            <Form.Group className="input-field">
                                <Form.Check
                                    type="checkbox"
                                    id="useExistingEmail"
                                    checked={formData.useExistingEmail}
                                    onChange={(e) => onInputChange('useExistingEmail', e.target.checked.toString())}
                                    label={
                                        <>
                                            {t('USE_EXISTING_EMAIL_ADDRESS') || 'Use existing email address'}
                                            {formData.contactEmail && (
                                                <span style={{ fontWeight: 'bold', fontStyle: 'italic' }}>
                                                    {` (${formData.contactEmail})`}
                                                </span>
                                            )}
                                        </>
                                    }
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    {!formData.useExistingEmail && (
                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Group className="input-field">
                                    <Form.Label>{t('SECONDARY_EMAIL_ADDRESS') || 'Secondary Email Address'}</Form.Label>
                                    <Form.Control
                                        type="email"
                                        className={errors.secondaryEmail ? 'is-invalid' : ''}
                                        placeholder={t('TYPE_HERE')}
                                        value={formData.secondaryEmail}
                                        onChange={(e) => onInputChange('secondaryEmail', e.target.value)}
                                    />
                                    {errors.secondaryEmail && (
                                        <Form.Text className="text-danger">{t(errors.secondaryEmail)}</Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                </>
            )}

            {/* E-invoice specific fields */}
            {formData.invoiceDeliveryMethod === '2' && (
                <>
                    <Row className="mb-4">
                        <Col md={4}>
                            <Form.Group className="input-field">
                                <Form.Label>{t('OUR_ELECTRONIC_REFERENCE')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={t('OUR_ELECTRONIC_REFERENCE')}
                                    value={formData.ourElectronicReference}
                                    onChange={(e) => onInputChange('ourElectronicReference', e.target.value)}
                                    readOnly
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="input-field">
                                <Form.Label>{t('YOUR_ELECTRONIC_REFERENCE')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    className={errors.yourElectronicReference ? 'is-invalid' : ''}
                                    placeholder={t('YOUR_ELECTRONIC_REFERENCE')}
                                    value={formData.yourElectronicReference}
                                    onChange={(e) => onInputChange('yourElectronicReference', e.target.value)}
                                />
                                {errors.yourElectronicReference && (
                                    <Form.Text className="text-danger">{t(errors.yourElectronicReference)}</Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="input-field">
                                <Form.Label>{t('GLN_PEPPOL_ID')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    className={errors.globalLocationNumber ? 'is-invalid' : ''}
                                    placeholder={t('GLN_PEPPOL_ID')}
                                    value={formData.globalLocationNumber}
                                    onChange={(e) => onInputChange('globalLocationNumber', e.target.value)}
                                    onInput={onGLNInput}
                                />
                                {errors.globalLocationNumber && (
                                    <Form.Text className="text-danger">{t(errors.globalLocationNumber)}</Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col md={6}>
                            <Form.Group className="input-field">
                                <Form.Label>{t('EXTRA_FIELD_1_OPTIONAL')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={t('EXTRA_FIELD_1_OPTIONAL')}
                                    value={formData.extraField1}
                                    onChange={(e) => onInputChange('extraField1', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="input-field">
                                <Form.Label>{t('EXTRA_FIELD_2_OPTIONAL')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={t('EXTRA_FIELD_2_OPTIONAL')}
                                    value={formData.extraField2}
                                    onChange={(e) => onInputChange('extraField2', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </>
            )}

            {/* Postal delivery specific fields */}
            {formData.invoiceDeliveryMethod === '3' && (
                <>
                    <Row className="mb-4">
                        <Col md={6}>
                            <Form.Group className="input-field">
                                <Form.Label>{t('CARE_OF')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={t('CARE_OF')}
                                    value={formData.careOf}
                                    onChange={(e) => onInputChange('careOf', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col md={12}>
                            <Form.Group className="input-field">
                                <Form.Check
                                    type="checkbox"
                                    id="useExistingAddress"
                                    checked={formData.useExistingAddress}
                                    onChange={(e) => onInputChange('useExistingAddress', e.target.checked.toString())}
                                    label={
                                        <>
                                            {t('USE_EXISTING_ADDRESS') || 'Use existing address for postal delivery'}
                                            {(formData.addressLine1 || formData.city || formData.zipCode || formData.country) && (
                                                <span style={{ fontWeight: 'bold', fontStyle: 'italic' }}>
                                                    {` (${[formData.addressLine1, formData.city, formData.zipCode, formData.country].filter(Boolean).join(', ')})`}
                                                </span>
                                            )}
                                        </>
                                    }
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    {!formData.useExistingAddress && (
                        <>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group className="input-field">
                                        <Form.Label>{t('POSTAL_ADDRESS') || 'Postal Address'}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className={errors.postAddress ? 'is-invalid' : ''}
                                            placeholder={t('TYPE_HERE')}
                                            value={formData.postAddress}
                                            onChange={(e) => onInputChange('postAddress', e.target.value)}
                                        />
                                        {errors.postAddress && (
                                            <Form.Text className="text-danger">{t(errors.postAddress)}</Form.Text>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="input-field">
                                        <Form.Label>{t('POSTAL_CITY') || 'Postal City'}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className={errors.postCity ? 'is-invalid' : ''}
                                            placeholder={t('TYPE_HERE')}
                                            value={formData.postCity}
                                            onChange={(e) => onInputChange('postCity', e.target.value)}
                                        />
                                        {errors.postCity && (
                                            <Form.Text className="text-danger">{t(errors.postCity)}</Form.Text>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={4}>
                                    <Form.Group className="input-field">
                                        <Form.Label>{t('POSTAL_COUNTRY') || 'Postal Country'}</Form.Label>
                                        <Form.Select
                                            className={`custom-select ${errors.postCountry ? 'is-invalid' : ''}`}
                                            value={formData.postCountry}
                                            onChange={(e) => onInputChange('postCountry', e.target.value)}
                                        >
                                            <option value="">{t('SELECT')}</option>
                                            {countryOptions.map((country) => (
                                                <option key={country.value} value={country.value}>
                                                    {country.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {errors.postCountry && (
                                            <Form.Text className="text-danger">{t(errors.postCountry)}</Form.Text>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="input-field">
                                        <Form.Label>{t('POSTAL_ZIP_CODE') || 'Postal Zip Code'}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className={errors.postZipCode ? 'is-invalid' : ''}
                                            placeholder={t('TYPE_HERE')}
                                            value={formData.postZipCode}
                                            onChange={(e) => onInputChange('postZipCode', e.target.value)}
                                        />
                                        {errors.postZipCode && (
                                            <Form.Text className="text-danger">{t(errors.postZipCode)}</Form.Text>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="input-field">
                                        <Form.Label>{t('POSTAL_COUNTRY_CODE') || 'Postal Country Code'}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className={errors.postCountryCode ? 'is-invalid' : ''}
                                            placeholder={t('TYPE_HERE')}
                                            value={formData.postCountryCode}
                                            onChange={(e) => onInputChange('postCountryCode', e.target.value)}
                                        />
                                        {errors.postCountryCode && (
                                            <Form.Text className="text-danger">{t(errors.postCountryCode)}</Form.Text>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default ContactInformation;